import { useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Loader2, Upload, X, Building } from 'lucide-react';
import { createOrganization } from '@/lib/firebase/queries/organizations';
import { storage } from '@/lib/firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';

const formSchema = z.object({
  name: z.string().min(2, 'Arabic name must be at least 2 characters'),
  nameEn: z.string().min(2, 'English name must be at least 2 characters'),
  description: z.string().optional(),
  logoUrl: z.string().optional()
});

interface AddOrganizationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function AddOrganizationDialog({
  open,
  onOpenChange,
  onSuccess
}: AddOrganizationDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { t, dir } = useLanguage();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      nameEn: '',
      description: '',
      logoUrl: ''
    }
  });

  const handleLogoSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select an image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size must be less than 5MB');
        return;
      }

      setLogoFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    setLogoFile(null);
    setLogoPreview('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadLogo = async (organizationId: string): Promise<string | null> => {
    if (!logoFile) return null;

    try {
      const logoRef = ref(storage, `organizations/${organizationId}/logo.${logoFile.name.split('.').pop()}`);
      const snapshot = await uploadBytes(logoRef, logoFile);
      const downloadURL = await getDownloadURL(snapshot.ref);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading logo:', error);
      throw new Error('Failed to upload logo');
    }
  };

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      setIsLoading(true);
      
      // Create organization first
      const organizationId = await createOrganization(values);
      
      // Upload logo if provided
      let logoUrl = null;
      if (logoFile) {
        logoUrl = await uploadLogo(organizationId);
        // Update organization with logo URL
        if (logoUrl) {
          const { updateOrganization } = await import('@/lib/firebase/queries/organizations');
          await updateOrganization(organizationId, { logoUrl });
        }
      }
      
      toast.success('Organization created successfully');
      form.reset();
      setLogoFile(null);
      setLogoPreview('');
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      console.error('Error creating organization:', error);
      toast.error('Failed to create organization');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent dir={dir}>
        <DialogHeader className={dir === 'rtl' ? 'text-right' : 'text-left'}>
          <DialogTitle>Add New Organization</DialogTitle>
          <DialogDescription>
            Create a new organization with basic information and optional logo.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" dir={dir}>
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className={dir === 'rtl' ? 'text-right' : 'text-left'}>Organization Name (Arabic)</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="أدخل اسم المنظمة" 
                      className={dir === 'rtl' ? 'text-right' : 'text-left'}
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage className={dir === 'rtl' ? 'text-right' : 'text-left'} />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="nameEn"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className={dir === 'rtl' ? 'text-right' : 'text-left'}>Organization Name (English)</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="Enter organization name" 
                      className={dir === 'rtl' ? 'text-right' : 'text-left'}
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage className={dir === 'rtl' ? 'text-right' : 'text-left'} />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className={dir === 'rtl' ? 'text-right' : 'text-left'}>Description (Optional)</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Enter organization description"
                      className={`resize-none ${dir === 'rtl' ? 'text-right' : 'text-left'}`}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage className={dir === 'rtl' ? 'text-right' : 'text-left'} />
                </FormItem>
              )}
            />

            {/* Logo Upload */}
            <div className="space-y-2">
              <FormLabel className={dir === 'rtl' ? 'text-right' : 'text-left'}>Organization Logo (Optional)</FormLabel>
              <div className={`flex ${dir === 'rtl' ? 'flex-row-reverse' : 'flex-row'} items-center gap-4`}>
                <div className="h-16 min-w-16 flex items-center justify-center">
                  {logoPreview ? (
                    <img 
                      src={logoPreview} 
                      alt="Organization logo" 
                      className="max-h-full max-w-full object-contain"
                    />
                  ) : (
                    <Building className="h-8 w-8 text-gray-400" />
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isLoading}
                  >
                    <Upload className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                    {logoFile ? t('common.changeLogo') || 'Change Logo' : t('common.uploadLogo') || 'Upload Logo'}
                  </Button>
                  {logoFile && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={removeLogo}
                      disabled={isLoading}
                    >
                      <X className={`h-4 w-4 ${dir === 'rtl' ? 'ml-2' : 'mr-2'}`} />
                      {t('common.remove') || 'Remove'}
                    </Button>
                  )}
                </div>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoSelect}
                className="hidden"
              />
            </div>

            <div className={`flex ${dir === 'rtl' ? 'justify-start' : 'justify-end'} gap-2`}>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className={`${dir === 'rtl' ? 'ml-2' : 'mr-2'} h-4 w-4 animate-spin`} />
                    Creating...
                  </>
                ) : (
                  'Create Organization'
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}